import { Action, GameState, Phase, RuleSet } from '../core/game.ts';
import { Emitter } from '../core/events.ts';

export enum GameMode {
  Solo = 'SOLO',
  Balance = 'BALANCE',
  Twin = 'TWIN',
}

interface SessionEvents extends Record<string, unknown> {
  onComplete: { mode: GameMode };
}

export class GameSession {
  readonly events = new Emitter<SessionEvents>();
  mode: GameMode = GameMode.Solo;

  private completionEmitted = false;

  constructor(readonly games: readonly [GameState, GameState]) {
    for (const game of this.games) game.events.on('onGameOver', () => this.checkComplete());
  }

  get activeGames(): readonly GameState[] {
    return this.mode === GameMode.Twin ? this.games : [this.games[0]];
  }

  get phase(): Phase {
    const games = this.activeGames;
    if (games.every((game) => game.phase === Phase.GameOver)) return Phase.GameOver;
    if (games.some((game) => game.phase === Phase.Playing)) return Phase.Playing;
    if (games.some((game) => game.phase === Phase.Paused)) return Phase.Paused;
    return Phase.Ready;
  }

  get score(): number {
    return this.activeGames.reduce((sum, game) => sum + game.score, 0);
  }

  get lines(): number {
    return this.activeGames.reduce((sum, game) => sum + game.lines, 0);
  }

  get level(): number {
    return Math.max(...this.activeGames.map((game) => game.level));
  }

  start(mode: GameMode = this.mode): void {
    this.mode = mode;
    this.completionEmitted = false;
    this.games[0].setRuleSet(mode === GameMode.Balance ? RuleSet.Balance : RuleSet.Standard);
    this.games[1].setRuleSet(RuleSet.Standard);
    for (const game of this.activeGames) game.start();
  }

  restart(): void {
    this.completionEmitted = false;
    for (const game of this.activeGames) game.restart();
  }

  applyInput(action: Action): void {
    if (action === Action.Restart) {
      this.restart();
      return;
    }

    for (const game of this.activeGames) game.applyInput(action);
    this.checkComplete();
  }

  step(dtMs: number): void {
    for (const game of this.activeGames) game.step(dtMs);
    this.checkComplete();
  }

  private checkComplete(): void {
    if (this.completionEmitted || this.phase !== Phase.GameOver) return;
    this.completionEmitted = true;
    this.events.emit('onComplete', { mode: this.mode });
  }
}
